import { useMemo } from 'react';

import { LAMINATE_REGEX } from '@common/constants';
import { MaterialModel } from '@common/models';
import { createSelectedHook } from '@core/utils/createSelectedHook';
import { selectRestorableMaterial, selectSelectedMaterial } from '@redux/slices/materialSlice';

import { findMaterialByAttributes } from '../../../pages/easy-estimator/components/scrollable-area/materials/materials.utils';
import useMaterials from './useMaterials';

const useMaterialsData = (): MaterialModel[] => {
    const { materials } = useMaterials();
    return materials;
};

export const selectDefaultMaterial = (materials: MaterialModel[], selectedValue: Partial<MaterialModel> | undefined) =>
    materials.find((material) => !!selectedValue && material.description === selectedValue?.description) ??
    materials[0];

const _useSelectedMaterial = createSelectedHook({
    selector: selectSelectedMaterial,
    restorableSelector: selectRestorableMaterial,
    apiQueryHook: useMaterialsData,
    selectRestoredValue: ({ apiHookResponse, restorableValue }) =>
        findMaterialByAttributes({
            materials: apiHookResponse,
            description: restorableValue?.description,
        }),
    /**
     * material.id changes on every api refetch, so a previous selection is not valid, BUT we will look for the same name
     * when selecting the default and use the new material with that name
     */
    shouldReturnDefault: ({ selectedValue, apiHookResponse }) =>
        !selectedValue || !apiHookResponse.find((material) => material.id === selectedValue?.id),
    /**
     * if there was a previous selection, but the ID's didn't match, check for the same name otherwise just pick the first one
     */
    selectDefaultValue: ({ apiHookResponse, selectedValue }) => selectDefaultMaterial(apiHookResponse, selectedValue),
});

export type SelectedMaterialReturn = {
    selectedValue: MaterialModel | undefined;
    isDefault: boolean;
    isLaminateSelected: boolean;
};

export const useSelectedMaterial = (): SelectedMaterialReturn => {
    const { materials } = useMaterials();
    const { selectedValue: _selectedValue, ...rest } = _useSelectedMaterial();

    // ensures that the returned value from the _useSelectedMaterial hook is the actual record that's in the materials list
    // for materials
    const selectedValue = useMemo(
        () => materials.find((material) => !!_selectedValue && material.id === _selectedValue?.id),
        [_selectedValue, materials]
    );

    const isLaminateSelected = !!selectedValue?.description?.match(LAMINATE_REGEX);

    return { isLaminateSelected, selectedValue, ...rest };
};
